import { Component, ElementRef, OnInit, ViewChild, signal, computed } from '@angular/core';
import { EditEmployeeDialogComponent } from './dialogs/user/user.component';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  isExcelFile,
  logExport,
  ucFirst,
  exportJsonToExcel,
  readExcelFile,
  valueToDate,
  translateAddressError,
} from '../globals';
import { FailedImportRow, User } from '../interfaces';
import { auth, dataStore, db, functions } from '../app.component';
import { collection, doc, getDoc, onSnapshot, query, or, where } from 'firebase/firestore';
import {
  ImportExportComponent,
  UserExportSelection,
} from '../settings/dialogs/import-export/import-export.component';
import { Role } from '../enums';
import moment from 'moment';
import { httpsCallable } from 'firebase/functions';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton, MatFabButton, MatButtonModule } from '@angular/material/button';
import { MatCard } from '@angular/material/card';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { CustomValidators } from 'angular-custom-validators';
import { AddressValidationService } from '../services/address-validation.service';
import { AddressData } from '../services/address-validation-flow.service';
import { MatTooltip } from '@angular/material/tooltip';
import { MatChip, MatChipSet } from '@angular/material/chips';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { WarningDialog } from '../global-dialogs/warning/warning.component';
import { PermissionsService } from '../services/permissions.service';
import { NgTemplateOutlet } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatMenuModule } from '@angular/material/menu';
import { MatDatepickerModule, MatDateRangePicker } from '@angular/material/datepicker';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MatProgressBar } from '@angular/material/progress-bar';

export interface UserId extends User {
  id: string;
}

type ImportRowResult = { success: true } | { success: false; reason: string };
type EmployeeDateField = 'startDate' | 'vogDate' | 'driversTestDate';

const importFailureReasons: Record<string, string> = {
  'no-email-or-phone-provided': 'Geen e-mailadres of telefoonnummer opgegeven',
  'auth/email-already-exists': 'E-mailadres is al in gebruik',
  'auth/phone-number-already-exists': 'Telefoonnummer is al in gebruik',
  email_already_exists: 'Het e-mailadres hoort niet bij het opgegeven medewerker-ID',
  user_not_found: 'Medewerker niet gevonden',
  'invalid-personal-data': 'Persoonsgegevens zijn ontbrekend of ongeldig',
  'personal-data-disabled': 'Persoonsgegevens beheren staat uit voor deze organisatie',
  'permission-denied': 'Onvoldoende rechten',
  unauthenticated: 'Niet ingelogd',
};

const personalDataLabels: Record<string, string> = {
  postal: 'Postcode',
  houseNumber: 'Huisnummer',
  streetName: 'Straatnaam',
  city: 'Woonplaats',
  startDate: 'Startdatum',
  vogDate: 'VOG-datum',
  driversTestDate: 'Rijtestdatum',
};

const personalDataFieldNames = [
  'postal',
  'houseNumber',
  'houseNumberAddition',
  'streetName',
  'city',
  'hiddenRemarks',
  'startDate',
  'vogDate',
  'driversTestDate',
  'addressCoordinates',
  'addressCoordinatesFor',
  'addressFingerprint',
  'location',
] as const;
const requiredPersonalDataFieldNames = personalDataFieldNames.filter(
  (field) =>
    field !== 'houseNumberAddition' &&
    field !== 'hiddenRemarks' &&
    field !== 'addressCoordinates' &&
    field !== 'addressCoordinatesFor' &&
    field !== 'addressFingerprint' &&
    field !== 'location',
);

@Component({
  selector: 'app-employees',
  templateUrl: './employees.component.html',
  styleUrls: ['./employees.component.scss'],
  imports: [
    MatIcon,
    MatIconButton,
    MatCard,
    MatFormField,
    MatLabel,
    MatInput,
    FormsModule,
    MatSuffix,
    MatTooltip,
    MatChip,
    MatChipSet,
    MatFabButton,
    MatProgressSpinner,
    MatProgressBar,
    NgTemplateOutlet,
    MatButtonModule,
    MatMenuModule,
    MatDatepickerModule,
    ReactiveFormsModule,
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'nl-NL' },
    provideMomentDateAdapter(
      {
        parse: { dateInput: 'DD-MM-YYYY' },
        display: {
          dateInput: 'DD-MM-YYYY',
          monthYearLabel: 'MMM YYYY',
          dateA11yLabel: 'LL',
          monthYearA11yLabel: 'MMMM YYYY',
        },
      },
      { strict: true },
    ),
  ],
})
export class EmployeesComponent implements OnInit {
  @ViewChild('uploader', { static: false }) uploader: ElementRef;
  loading = signal(true);
  loadingPage = signal(true);
  hasPersonalDataAccess = signal(false);
  canShowEmployeePhotos = signal(false);
  isAdministrator = signal(false);
  displayedColumns = signal<string[]>([]);
  searchQuery = signal('');
  orderBy = signal('name');
  orderDirection = signal('asc');

  plannerEmail: string;

  organisationId = localStorage.getItem('orgId');
  showHidden = signal(false);
  rights = signal<string>(undefined);

  readonly filterRoles = [
    Role.Administrator,
    Role.Planner,
    Role.RidePlanner,
    Role.Driver,
    Role.CoDriver,
    Role.Other,
  ];
  selectedRoles = signal<Role[]>([...this.filterRoles]);

  toggleRole(role: Role) {
    this.selectedRoles.update((roles) =>
      roles.includes(role) ? roles.filter((selected) => selected !== role) : [...roles, role],
    );
  }

  readonly dateFields: { value: EmployeeDateField; label: string }[] = [
    { value: 'startDate', label: 'Startdatum' },
    { value: 'vogDate', label: 'VOG-datum' },
    { value: 'driversTestDate', label: 'Rijtestdatum' },
  ];
  readonly dateRangeDrafts = {
    startDate: this.createDateRangeControls(),
    vogDate: this.createDateRangeControls(),
    driversTestDate: this.createDateRangeControls(),
  };

  private createDateRangeControls() {
    return {
      start: new FormControl<moment.Moment | null>(null),
      end: new FormControl<moment.Moment | null>(null),
    };
  }

  openDateFilter(field: EmployeeDateField, picker: MatDateRangePicker<moment.Moment>) {
    if (!this.isAdministrator() || !this.hasPersonalDataAccess()) {
      return;
    }
    const draft = this.dateRangeDrafts[field];
    draft.start.setValue(this.selectedDateField() === field ? this.dateStartControl.value : null);
    draft.end.setValue(this.selectedDateField() === field ? this.dateEndControl.value : null);
    picker.open();
  }

  applyDateFilter(field: EmployeeDateField) {
    const { start, end } = this.dateRangeDrafts[field];
    if (
      !this.isAdministrator() ||
      !this.hasPersonalDataAccess() ||
      !start.value?.isValid() ||
      !end.value?.isValid() ||
      end.value.isBefore(start.value, 'day')
    ) {
      return;
    }
    this.selectedDateField.set(field);
    this.dateStartControl.setValue(start.value.clone());
    this.dateEndControl.setValue(end.value.clone());
  }

  selectedDateField = signal<EmployeeDateField | null>(null);
  readonly dateStartControl = new FormControl<moment.Moment | null>(null, Validators.required);
  readonly dateEndControl = new FormControl<moment.Moment | null>(null, Validators.required);
  private readonly dateStart = toSignal(this.dateStartControl.valueChanges, { initialValue: null });
  private readonly dateEnd = toSignal(this.dateEndControl.valueChanges, { initialValue: null });
  readonly dateRangeReversed = computed(() => {
    const start = this.dateStart();
    const end = this.dateEnd();
    return !!(start?.isValid() && end?.isValid() && end.isBefore(start, 'day'));
  });
  readonly activeDateRange = computed(() => {
    const field = this.selectedDateField();
    const start = this.dateStart();
    const end = this.dateEnd();
    if (
      !this.hasPersonalDataAccess() ||
      !field ||
      !start?.isValid() ||
      !end?.isValid() ||
      this.dateRangeReversed()
    ) {
      return null;
    }
    return { field, start: this.calendarDay(start.toDate()), end: this.calendarDay(end.toDate()) };
  });
  readonly roleFilterActive = computed(
    () => !this.filterRoles.every((role) => this.selectedRoles().includes(role)),
  );
  readonly roleFilterSummary = computed(() => {
    if (!this.roleFilterActive()) {
      return 'Alle rollen';
    }
    return (
      this.selectedRoles()
        .map((role) => this.getRoleLabel(role))
        .join(', ') || 'Geen rollen geselecteerd'
    );
  });
  readonly dateFilterSummary = computed(() => {
    if (!this.hasPersonalDataAccess()) {
      return '';
    }
    const field = this.dateFields.find((option) => option.value === this.selectedDateField());
    const start = this.dateStart();
    const end = this.dateEnd();
    if (!field && !start && !end) {
      return 'Geen datumfilter';
    }
    const startLabel = start?.isValid() ? start.format('DD-MM-YYYY') : '...';
    const endLabel = end?.isValid() ? end.format('DD-MM-YYYY') : '...';
    return `${field?.label ?? 'Geen datumveld'}: ${startLabel} t/m ${endLabel}${this.activeDateRange() ? '' : ' (niet actief)'}`;
  });

  allUsers = signal<User[]>([]);
  filteredUsers = computed(() => {
    const searchQuery = this.searchQuery().toLowerCase();
    const showHidden = this.showHidden();
    const isAdministrator = this.isAdministrator();

    const filtered = this.allUsers().filter((user) => {
      if (!isAdministrator && ((user.inactive ?? false) || user.superuser === true)) {
        return false;
      }

      let passesSearchFilter = true;
      if (isAdministrator && searchQuery) {
        passesSearchFilter = false;
        const searchableValues = [user.name, user.email];
        if (this.hasPersonalDataAccess()) {
          searchableValues.push(
            user.postal,
            [user.postal, user.houseNumber, user.houseNumberAddition].filter(Boolean).join(' '),
            user.streetName,
            user.city,
            this.formatDateForSearch(user.startDate),
            this.formatDateForSearch(user.vogDate),
            this.formatDateForSearch(user.driversTestDate),
          );
        }
        if (
          searchableValues.some(
            (value) => typeof value === 'string' && value.toLowerCase().includes(searchQuery),
          )
        ) {
          passesSearchFilter = true;
        }
      }

      let finalPass = false;
      const isInactive = user.inactive ?? false;

      if (isAdministrator && ((showHidden && isInactive) || (!showHidden && !isInactive))) {
        finalPass = passesSearchFilter;
      } else if (!isAdministrator && !isInactive) {
        finalPass = true;
      }

      return finalPass;
    });

    if (!isAdministrator) {
      return filtered;
    }
    const selectedRoles = this.selectedRoles();
    const allRolesSelected = this.filterRoles.every((role) => selectedRoles.includes(role));
    const dateRange = this.activeDateRange();
    return filtered.filter((user) => {
      if (!allRolesSelected && !user.roles?.some((role) => selectedRoles.includes(role as Role))) {
        return false;
      }
      if (dateRange) {
        const day = this.calendarDay(user[dateRange.field]);
        return day !== null && day >= dateRange.start && day <= dateRange.end;
      }
      return true;
    });
  });

  clearRoleFilter() {
    this.selectedRoles.set([...this.filterRoles]);
  }

  clearDateFilter() {
    this.selectedDateField.set(null);
    this.dateStartControl.reset();
    this.dateEndControl.reset();
    Object.values(this.dateRangeDrafts).forEach((draft) => {
      draft.start.reset();
      draft.end.reset();
    });
  }

  clearAllFilters() {
    this.clearRoleFilter();
    this.clearDateFilter();
  }

  private calendarDay(value: unknown): number | null {
    try {
      if (value === null || value === undefined || value === '' || typeof value === 'boolean') {
        return null;
      }
      const converted = typeof value === 'string' ? value : valueToDate(value);
      if (typeof converted !== 'string' && !(converted instanceof Date)) {
        return null;
      }
      const date =
        typeof converted === 'string'
          ? moment(converted, moment.ISO_8601, true)
          : moment(converted);
      return date.isValid() ? date.year() * 10000 + (date.month() + 1) * 100 + date.date() : null;
    } catch {
      return null;
    }
  }

  processing = signal(false);
  importing = signal(false);
  importProgress = signal(0);
  fileUploaded: any;

  getRoleLabel(role: string): string {
    const labels: Record<string, string> = {
      administrator: 'Administrator',
      planner: 'Planner',
      rideplanner: 'Ritplanner',
      driver: 'Chauffeur',
      co_driver: 'Bijrijder',
      other: 'Overige',
      inactive: 'Inactief',
    };
    return labels[role] || role;
  }

  constructor(
    public dialog: MatDialog,
    private router: Router,
    private snackBar: MatSnackBar,
    public addressValidationService: AddressValidationService,
    private permissionsService: PermissionsService,
  ) {}

  async ngOnInit() {
    if (!auth.currentUser) {
      this.router.navigateByUrl('/login');
      this.loadingPage.set(false);
      return;
    }

    const userDoc = await getDoc(doc(db, `users/${auth.currentUser.uid}`));
    const loggedInUser = { ...userDoc.data(), id: userDoc.id } as User;
    const organisationId = this.organisationId ?? dataStore.selectedOrganisation?.id;
    const organisationUserDoc = await getDoc(
      doc(db, `organisations/${organisationId}/users/${auth.currentUser.uid}`),
    );
    const organisationUser = organisationUserDoc.data() as User | undefined;
    const organisationDoc = await getDoc(doc(db, `organisations/${organisationId}`));
    const paidFunctionalities = organisationDoc.data()?.paidFunctionalities ?? {};
    this.canShowEmployeePhotos.set(paidFunctionalities.usesEmployeeListForStaff === true);
    const roles = organisationUser?.roles ?? [];
    const isAdministrator =
      loggedInUser.rights === 'admin' || this.permissionsService.hasRole(roles, Role.Administrator);
    const isInternalStaff = roles.some((role) =>
      [Role.Planner, Role.RidePlanner, Role.Driver, Role.CoDriver, Role.Other].includes(role),
    );
    const canView =
      isAdministrator || (isInternalStaff && paidFunctionalities.usesEmployeeListForStaff === true);

    if (!canView) {
      this.router.navigateByUrl(
        this.permissionsService.getDefaultRoute(auth.currentUser.uid, roles),
      );
      this.loadingPage.set(false);
      return;
    }

    this.organisationId = organisationId;
    this.plannerEmail = loggedInUser.email;
    this.rights.set(loggedInUser.rights);
    this.isAdministrator.set(isAdministrator);
    this.displayedColumns.set(
      isAdministrator
        ? [
            'photo',
            'name',
            'email',
            'phone',
            ...(dataStore.paidFunctionalities?.usesEmployeePersonalDataManagement
              ? ['startDate', 'vogDate', 'driversTestDate', 'roles', 'hiddenRemarks']
              : ['roles']),
          ]
        : ['photo', 'name', 'phone'],
    );
    const personalDataAccess =
      dataStore.paidFunctionalities?.usesEmployeePersonalDataManagement ?? false;
    this.hasPersonalDataAccess.set(personalDataAccess);

    const staffRoles = Object.values(Role);
    const employeesQuery = isAdministrator
      ? query(
          collection(db, `organisations/${this.organisationId}/users`),
          or(
            where('roles', 'array-contains-any', staffRoles),
            where('superuser', '==', true),
            where('inactive', '==', true),
          ),
        )
      : query(
          collection(db, `organisations/${this.organisationId}/users`),
          where('roles', 'array-contains-any', staffRoles),
          where('inactive', '==', false),
        );

    onSnapshot(employeesQuery, (snapshot) => {
      const allUsers: User[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data() as User;
        const id = doc.id;

        const roles = data.roles ?? [];
        const isInactive = data.inactive ?? false;
        const isDeleted = data.deleted ?? false;
        const hasStaffRole =
          roles.some((role) => staffRoles.includes(role as Role)) || data.superuser;

        const isEmployee = (hasStaffRole || isInactive) && !isDeleted;

        if (isEmployee) {
          data.name = ucFirst(data?.name ?? '');
          allUsers.push({ id, ...data });
        }
      });

      allUsers.sort((a, b) => {
        const nameA = a.name ? a.name.toLowerCase() : '';
        const nameB = b.name ? b.name.toLowerCase() : '';
        if (nameA > nameB) return 1;
        if (nameA < nameB) return -1;
        return 0;
      });

      this.allUsers.set(allUsers);
      this.loading.set(false);
    });
    this.loadingPage.set(false);
  }

  clearSearch() {
    this.searchQuery.set('');
  }

  openImportExport() {
    const dialogRef = this.dialog.open(ImportExportComponent, {
      width: '450px',
      data: {
        type: 'admin-users',
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result.type === 'Export' && result.usersToExport) {
        this.export(result.usersToExport);
      }
      if (result.type === 'Import') {
        this.uploader.nativeElement.click();
      }
    });
  }

  uploadedFile(event) {
    if (this.uploader.nativeElement.value === null) {
      return;
    } else {
      if (isExcelFile(event.target.files[0])) {
        const readFile = new FileReader();
        readFile.onload = async () => {
          this.fileUploaded = await readExcelFile(event.target.files[0]);
          this.import();
          this.uploader.nativeElement.value = ''; // Reset input
        };
        readFile.readAsArrayBuffer(event.target.files[0]);
      } else {
        this.snackBar.open('Het geselecteerde bestand moet een Excel-bestand zijn.', 'X', {
          duration: 3000,
        });
        this.uploader.nativeElement.value = '';
      }
    }
  }

  async export(exportType: UserExportSelection) {
    if (this.processing()) {
      return;
    }
    this.processing.set(true);

    const usersExcel = [];
    let usersToExport: User[];
    switch (exportType) {
      case 'filtered':
        usersToExport = this.filteredUsers();
        break;
      case 'current':
        usersToExport = this.allUsers().filter(
          (user) => (user.inactive ?? false) === this.showHidden(),
        );
        break;
      case 'all':
      default:
        usersToExport = this.allUsers();
        break;
    }
    usersToExport = usersToExport.filter((user) => !user.superuser);

    usersToExport.forEach((element: any) => {
      let createdAt = element.createdAt ? element.createdAt.toDate() : 'N.V.T';
      if (createdAt !== 'N.V.T') {
        createdAt = moment(createdAt).format('DD-MM-YYYY HH:mm');
      }

      const roles = (element.roles || []).filter(
        (r: any) => r !== 'passenger' && r !== 'passagier',
      );

      let roleStr = roles.map((r) => this.getRoleLabel(r)).join(', ');
      if (element.passenger) {
        roleStr = roleStr ? `${roleStr}, Passagier` : 'Passagier';
      }
      if (roleStr === '') {
        roleStr = 'Geen';
      }

      const objectToPush = {
        'Medewerker ID': element.id,
        'Gemaakt op': createdAt,
        Rol: roleStr,
        Naam: element.name ? element.name : 'N.V.T',
        'E-mail': element.email ? element.email : 'N.V.T',
        Telefoonnummer: element.phone ? element.phone : 'N.V.T',
        Inlogmethode: element.signInMethod ? element.signInMethod : 'N.V.T',
        'Medewerker Verstopt': element.hidden ? 'Ja' : 'Nee',
        'Medewerker Ook Passagier': element.passenger ? 'Ja' : 'Nee',
        ...(this.hasPersonalDataAccess()
          ? {
              Postcode: element.postal ?? '',
              Huisnummer: element.houseNumber ?? '',
              Toevoeging: element.houseNumberAddition ?? '',
              Straatnaam: element.streetName ?? '',
              Woonplaats: element.city ?? '',
              'Opmerking (Algemeen)': element.hiddenRemarks ?? '',
              Startdatum: this.formatDate(element.startDate),
              'VOG-datum': this.formatDate(element.vogDate),
              Rijtestdatum: this.formatDate(element.driversTestDate),
            }
          : {}),
      };
      usersExcel.push(objectToPush);
    });
    await exportJsonToExcel(usersExcel, 'Medewerkers', 'Medewerkers');
    await logExport('admin-users');
    this.processing.set(false);
  }

  async import() {
    if (this.processing()) {
      return;
    }
    if (!this.fileUploaded || Object.keys(this.fileUploaded).length === 0) {
      this.snackBar.open('Het bestand is leeg of kon niet worden gelezen.', 'X', {
        duration: 3000,
      });
      return;
    }

    this.processing.set(true);
    this.importing.set(true);
    this.importProgress.set(0);

    try {
      // Process worksheet
      const importRows: {
        name: string;
        originalRow: Record<string, any>;
        userId?: string;
        userData: UserId & AddressData;
        fullAddress?: string;
        run: () => Promise<ImportRowResult>;
      }[] = [];
      const addressesToValidate = new Set<string>();
      const addressFailures = new Map<string, string>();
      const validationResultsByAddress = new Map<string, any>();
      // The spreadsheet is already parsed by readExcelFile into a nested object: { rowId: { colId: value } }
      const spreadsheet = this.fileUploaded;

      const columnNames = spreadsheet[1];
      if (!columnNames) {
        this.snackBar.open('Geen kolomnamen gevonden op de eerste rij.', 'X', {
          duration: 3000,
        });
        return;
      }
      const originalColumnNames = { ...columnNames };
      const buildOriginalRow = (row: Record<string, any>): Record<string, any> => {
        const originalRow: Record<string, any> = {};
        Object.keys(row).forEach((columnKey) => {
          const header = originalColumnNames[columnKey];
          if (
            header &&
            header.toString().trim().toLowerCase() !== 'reden van fout bij importeren'
          ) {
            originalRow[header] = row[columnKey];
          }
        });
        return originalRow;
      };

      Object.keys(columnNames).forEach((key) => {
        const cellValue = columnNames[key];
        if (!cellValue) {
          delete columnNames[key];
          return;
        }
        const val = cellValue.toString().toLowerCase().trim();
        switch (val) {
          case 'medewerker id':
            columnNames[key] = 'userId';
            break;
          case 'rol':
            columnNames[key] = 'roles';
            break;
          case 'naam':
            columnNames[key] = 'name';
            break;
          case 'e-mail':
            columnNames[key] = 'email';
            break;
          case 'telefoonnummer':
            columnNames[key] = 'phone';
            break;
          case 'inlogmethode':
            columnNames[key] = 'signInMethod';
            break;
          case 'medewerker verstopt':
            columnNames[key] = 'hidden';
            break;
          case 'medewerker ook passagier':
            columnNames[key] = 'passenger';
            break;
          case 'postcode':
          case 'postal':
            columnNames[key] = 'postal';
            break;
          case 'huisnummer':
          case 'housenumber':
            columnNames[key] = 'houseNumber';
            break;
          case 'toevoeging':
          case 'huisnummer toevoeging':
          case 'housenumberaddition':
            columnNames[key] = 'houseNumberAddition';
            break;
          case 'straatnaam':
          case 'streetname':
            columnNames[key] = 'streetName';
            break;
          case 'woonplaats':
          case 'stad':
          case 'city':
            columnNames[key] = 'city';
            break;
          case 'opmerking (algemeen)':
          case 'hiddenremarks':
            columnNames[key] = 'hiddenRemarks';
            break;
          case 'startdatum':
          case 'startdate':
            columnNames[key] = 'startDate';
            break;
          case 'vog-datum':
          case 'vogdate':
            columnNames[key] = 'vogDate';
            break;
          case 'rijtestdatum':
          case 'driverstestdate':
            columnNames[key] = 'driversTestDate';
            break;
        }
      });
      delete spreadsheet[1];
      Object.keys(spreadsheet).forEach((key) => {
        let userId: string;
        const invalidDateFields: string[] = [];
        const userData = {} as UserId & AddressData;
        Object.keys(spreadsheet[key]).forEach((colKey) => {
          const colName = columnNames[colKey];
          const rawValue = spreadsheet[key][colKey];
          if (
            colName &&
            rawValue !== undefined &&
            rawValue !== null &&
            rawValue.toString().length !== 0
          ) {
            const value = rawValue;
            if (value && !value.toString().includes('N.V.T')) {
              switch (colName) {
                case 'userId':
                  userId = spreadsheet[key][colKey];
                  break;
              }
            }
            if (value !== undefined && value !== null) {
              if (
                personalDataFieldNames.includes(
                  colName as (typeof personalDataFieldNames)[number],
                ) &&
                !this.hasPersonalDataAccess()
              ) {
                return;
              }
              switch (colName) {
                case 'hidden':
                case 'passenger': {
                  const boolVal = value.toString().toLowerCase().trim();
                  userData[colName] = boolVal === 'ja' || boolVal === 'true' || boolVal === 'waar';
                  break;
                }
                case 'roles': {
                  const roleLabels = value
                    .toString()
                    .split(',')
                    .map((s) => s.trim().toLowerCase());
                  userData[colName] = roleLabels
                    .map((label) => {
                      switch (label) {
                        case 'administrator':
                          return Role.Administrator;
                        case 'planner':
                          return Role.Planner;
                        case 'ritplanner':
                        case 'rideplanner':
                          return Role.RidePlanner;
                        case 'chauffeur':
                        case 'driver':
                          return Role.Driver;
                        case 'bijrijder':
                        case 'co-driver':
                        case 'co_driver':
                          return Role.CoDriver;
                        case 'overige':
                          return Role.Other;
                        default:
                          return null;
                      }
                    })
                    .filter((r) => r !== null);
                  break;
                }
                case 'signInMethod':
                  userData[colName] = value.toString().includes('N.V.T') ? 'email' : value;
                  break;
                case 'userId':
                  break;
                case 'startDate':
                case 'vogDate':
                case 'driversTestDate': {
                  const parsedDate = this.parseImportDate(value);
                  if (
                    !parsedDate ||
                    (colName !== 'startDate' && parsedDate > this.todayDateOnly())
                  ) {
                    invalidDateFields.push(colName);
                  } else {
                    userData[colName] = parsedDate;
                  }
                  break;
                }
                default:
                  userData[colName] = value;
                  break;
              }
            }
          }
        });
        const rowName = userData.name || userData.email || `Rij ${key}`;
        const originalRow = buildOriginalRow(spreadsheet[key]);
        const reject = (reason: string) =>
          importRows.push({
            name: rowName,
            originalRow,
            userId,
            userData,
            run: () => Promise.resolve({ success: false, reason }),
          });
        if (!userData.email) {
          reject('Geen e-mailadres opgegeven');
          return;
        }
        if (this.hasPersonalDataAccess()) {
          const missingFields = requiredPersonalDataFieldNames.filter(
            (field) => !userData[field] && !invalidDateFields.includes(field),
          );
          const problems = [
            ...missingFields.map((field) => `${personalDataLabels[field]} ontbreekt`),
            ...invalidDateFields.map((field) =>
              field === 'startDate'
                ? `${personalDataLabels[field]} is ongeldig`
                : `${personalDataLabels[field]} is ongeldig of ligt in de toekomst`,
            ),
          ];
          if (userData.postal && CustomValidators.postal(new FormControl(userData.postal))) {
            problems.push('Postcode is ongeldig');
          }
          if (
            userData.houseNumber &&
            CustomValidators.numberInput(false, false, 0)(new FormControl(userData.houseNumber))
          ) {
            problems.push('Huisnummer is ongeldig');
          }
          if (problems.length > 0) {
            reject(problems.join(', '));
            return;
          }
        }
        const fullAddress = this.hasPersonalDataAccess()
          ? `${userData.streetName} ${[userData.houseNumber, userData.houseNumberAddition].filter(Boolean).join(' ')}, ${userData.postal} ${userData.city}`
          : null;
        const run = async (): Promise<ImportRowResult> => {
          if (fullAddress && addressFailures.has(fullAddress)) {
            return { success: false, reason: addressFailures.get(fullAddress)! };
          }
          try {
            if (fullAddress) {
              const validation = validationResultsByAddress.get(fullAddress);
              if (validation?.coordinates) {
                const addressFingerprint = getAddressFingerprint(userData);
                userData.addressCoordinates = { ...validation.coordinates };
                userData.addressCoordinatesFor = addressFingerprint;
                userData.addressFingerprint = addressFingerprint;
                userData.location = {
                  _lat: validation.coordinates.latitude,
                  _long: validation.coordinates.longitude,
                };
              }
            }
            const callable = httpsCallable(functions, 'httpAddUser');
            const addUserData = { ...userData };
            for (const field of personalDataFieldNames) delete addUserData[field];
            const result: any = await callable({
              userId: userId ?? null,
              email: userData.email,
              phoneNumber: userData.phone,
              organisationId: this.organisationId,
              passenger: userData.passenger ?? false,
              isEmployeeManagement: true,
              saveObj: addUserData,
            });
            if (result?.data?.message !== 'succeed') {
              return { success: false, reason: this.getImportFailureReason(result?.data?.message) };
            }
            const rightsResult = await this.grantRights({
              ...userData,
              isNewUser: false,
              isEmployeeImport: true,
              id: result.data.userId,
            });
            if (rightsResult !== 'success') {
              return { success: false, reason: this.getImportFailureReason(rightsResult) };
            }
            return { success: true };
          } catch (error: any) {
            console.error(`Import error for ${rowName}:`, error);
            return { success: false, reason: this.getImportFailureReason(error?.message) };
          }
        };
        importRows.push({ name: rowName, originalRow, userId, userData, fullAddress, run });
      });

      const existingEmployeeSnapshots = new Map<string, any>();
      const referencedEmployeeIds = [
        ...new Set(importRows.map((row) => row.userId).filter(Boolean) as string[]),
      ];
      try {
        await Promise.all(
          referencedEmployeeIds.map(async (userId) => {
            existingEmployeeSnapshots.set(
              userId,
              await getDoc(doc(db, `organisations/${this.organisationId}/users/${userId}`)),
            );
          }),
        );
      } catch (error) {
        console.error('Failed to load existing employees:', error);
        this.snackBar.open('Er ging iets mis bij het ophalen van bestaande medewerkers.', 'X', {
          duration: 5000,
        });
        return;
      }

      const getAddressFingerprint = (userData: UserId & AddressData): string =>
        this.addressValidationService.normalizeAddressForComparison(
          `${userData.streetName ?? ''} ${userData.houseNumber ?? ''} ${userData.houseNumberAddition ?? ''}, ${userData.postal ?? ''} ${userData.city ?? ''}`,
        );

      const reuseExistingCoordinates = (
        userData: UserId & AddressData,
        existingUserData: User,
      ): boolean => {
        const coordinates = existingUserData.addressCoordinates;
        const importedAddress = getAddressFingerprint(userData);
        const existingAddress = getAddressFingerprint(existingUserData as UserId & AddressData);
        const storedAddress =
          existingUserData.addressCoordinatesFor ?? existingUserData.addressFingerprint;

        if (
          !coordinates ||
          !storedAddress ||
          importedAddress !== existingAddress ||
          importedAddress !== storedAddress
        ) {
          return false;
        }

        userData.addressCoordinates = { ...coordinates };
        userData.addressCoordinatesFor = importedAddress;
        userData.addressFingerprint = importedAddress;
        userData.location = {
          _lat: coordinates.latitude,
          _long: coordinates.longitude,
        };
        return true;
      };

      importRows.forEach((row) => {
        if (!row.fullAddress) return;
        const existingSnapshot = row.userId ? existingEmployeeSnapshots.get(row.userId) : undefined;
        const hasReusableCoordinates =
          existingSnapshot?.exists() &&
          reuseExistingCoordinates(row.userData, existingSnapshot.data() as User);
        if (!hasReusableCoordinates) addressesToValidate.add(row.fullAddress);
      });

      if (addressesToValidate.size > 0) {
        try {
          const validation = await this.addressValidationService.validateAddressesBulk([
            ...addressesToValidate,
          ]);
          for (const address of addressesToValidate) {
            if (!validation.validAddresses.some((result) => result.originalAddress === address)) {
              const invalid = validation.invalidAddresses.find(
                (result) => result.originalAddress === address,
              );
              addressFailures.set(
                address,
                invalid?.error
                  ? translateAddressError(invalid.error)
                  : invalid?.requiresConfirmation
                    ? 'Adres niet geïmporteerd: ORS kon het adres niet volledig verifiëren'
                    : 'Adres niet gevonden via ORS',
              );
            } else {
              const validResult = validation.validAddresses.find(
                (result) => result.originalAddress === address,
              );
              if (validResult) validationResultsByAddress.set(address, validResult);
            }
          }
        } catch (error) {
          console.error('Failed to validate employee import addresses:', error);
          for (const address of addressesToValidate) {
            addressFailures.set(address, 'Adrescontrole mislukt; probeer de import opnieuw');
          }
        }
      }
      let completedImports = 0;
      const results = await Promise.all(
        importRows.map((row) =>
          row.run().finally(() => {
            completedImports++;
            this.importProgress.set(
              importRows.length ? Math.round((completedImports / importRows.length) * 100) : 100,
            );
          }),
        ),
      );

      const failedRows: (FailedImportRow & { originalRow: Record<string, any> })[] = [];
      results.forEach((result, index) => {
        if (result.success === false) {
          const { name, originalRow } = importRows[index];
          failedRows.push({ name, user: name, error: result.reason, originalRow });
        }
      });
      const successCount = results.length - failedRows.length;

      let failedRowsDownloadSucceeded = true;
      if (failedRows.length > 0) {
        try {
          await exportJsonToExcel(
            failedRows.map(({ originalRow, error }) => ({
              ...originalRow,
              'Reden van fout bij importeren': error,
            })),
            'Medewerkers-import-fouten',
            'Medewerkers',
          );
        } catch (error) {
          failedRowsDownloadSucceeded = false;
          console.error('Failed to export employee import errors:', error);
        }
      }

      if (failedRows.length > 0 && successCount > 0) {
        this.snackBar.open(
          `Het importeren van de medewerkers is gedeeltelijk voltooid: ${successCount} gelukt, ${failedRows.length} mislukt.`,
          'X',
          { duration: 30000 },
        );
      } else if (failedRows.length > 0) {
        this.snackBar.open(
          `Het importeren van de medewerkers is mislukt: ${failedRows.length} mislukt.`,
          'X',
          { duration: 30000 },
        );
      } else {
        this.snackBar.open('Het importeren van de medewerkers is voltooid.', 'X', {
          duration: 3000,
        });
      }
      if (failedRows.length > 0) {
        this.dialog.open(WarningDialog, {
          width: '700px',
          data: {
            dialogType: 'importFailedRows',
            optionalData: {
              failedRows,
              hasSuccessfulImports: successCount > 0,
              downloadSucceeded: failedRowsDownloadSucceeded,
            },
          },
        });
      }
    } catch (error) {
      console.error('Employee import error:', error);
      this.snackBar.open('Er is iets misgegaan tijdens het importeren van de medewerkers.', 'X', {
        duration: 5000,
      });
    } finally {
      this.processing.set(false);
      this.importing.set(false);
      this.importProgress.set(0);
      if (this.uploader?.nativeElement) {
        this.uploader.nativeElement.value = '';
      }
    }
  }

  private getImportFailureReason(message: unknown): string {
    return (
      (typeof message === 'string' && importFailureReasons[message]) ||
      'Medewerker kon niet worden geïmporteerd'
    );
  }

  async editUser(user?) {
    this.dialog.open(EditEmployeeDialogComponent, {
      panelClass: 'fullscreen-dialog',
      autoFocus: false,
      data: { user },
    });
  }

  private async grantRights(userData: any): Promise<any> {
    const callable = httpsCallable(functions, 'httpGrantRights');
    const params = {
      email: userData.email,
      password: null,
      name: ucFirst(userData.name || ''),
      roles: userData.roles || [],
      organisationId: this.organisationId,
      phone: userData.phone || userData.phoneNumber || '',
      hidden: userData.hidden ?? false,
      withdrawRights: false,
      passenger: userData.passenger ?? false,
      userId: userData.id ?? null,
      isEmployeeManagement: true,
      isEmployeeImport: userData.isEmployeeImport === true,
      ...(this.hasPersonalDataAccess()
        ? Object.fromEntries(
            personalDataFieldNames.map((field) => [
              field,
              field.endsWith('Date')
                ? this.formatDate(userData[field], 'YYYY-MM-DD')
                : userData[field],
            ]),
          )
        : {}),
    };

    const result = await callable(params);
    return result.data;
  }

  formatDate(value: any, format = 'DD-MM-YYYY'): string {
    const date = valueToDate(value);
    return date ? moment(date).format(format) : '';
  }

  private formatDateForSearch(value: any): string | undefined {
    const date = valueToDate(value);
    return date
      ? `${moment(date).format('DD-MM-YYYY')} ${moment(date).format('YYYY-MM-DD')}`
      : undefined;
  }

  private parseImportDate(value: any): string | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      // Excel serial date: days since 1899-12-30
      return moment.utc('1899-12-30').add(value, 'days').format('YYYY-MM-DD');
    }
    if (value instanceof Date) {
      const date = valueToDate(value);
      return date ? moment(date).format('YYYY-MM-DD') : null;
    }
    const parsed = moment(value, ['DD-MM-YYYY', 'YYYY-MM-DD'], true);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
  }

  private todayDateOnly(): string {
    return moment().format('YYYY-MM-DD');
  }
}
